import { IconButton } from "@carbon6/c6-frontend-design";
import { useId, type ReactNode } from "react";
import { NOTE_MAX_LENGTH } from "@/api";
import { Editor, useEditorController } from "@/components/editor";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import type { NoteController } from "./useNoteController";
import styles from "./NotePanel.module.css";

// 단일 노트 편집 화면이다. 로드한 note 를 textarea 로 편집한다(저장/지우기). c6 에 멀티라인 입력이
// 없으므로 native textarea 를 쓴다(MemoComposer 와 같은 방식).
export function NotePanel({ controller }: { controller: NoteController }): ReactNode {
  const { state, saving } = controller;
  const inputId = useId();
  const loadedNote = state.status === "success" ? (state.data.note ?? "") : "";
  const editor = useEditorController({
    value: state.status === "success" ? loadedNote : null,
    disabled: saving,
    isEqual: equalTrimmed,
    onSave: async (draft) => {
      const saved = await controller.onSave(draft);
      return saved ? { ok: true } : { ok: false };
    },
  });

  if (state.status === "idle" || state.status === "loading") {
    return <LoadingState size="sm">노트를 불러오고 있습니다.</LoadingState>;
  }
  if (state.status === "error") {
    return (
      <EmptyState
        size="sm"
        tone="error"
        action={{ label: "다시 시도", onClick: controller.reload }}
      >
        {state.message}
      </EmptyState>
    );
  }

  const trimmed = editor.value.trim();
  const saveDisabled = editor.inputDisabled || trimmed === "" || !editor.dirty;
  const clearDisabled = editor.inputDisabled || loadedNote === "";

  async function handleSave(): Promise<void> {
    await editor.save();
  }

  async function handleClear(): Promise<void> {
    const cleared = await controller.onClear();
    if (cleared) editor.replace("");
  }

  return (
    <section className={styles.panel} aria-label="Session 노트">
      <label htmlFor={inputId} className={styles.label}>
        노트
      </label>
      <Editor
        controller={editor}
        id={inputId}
        className={styles.input}
        rows={5}
        maxLength={NOTE_MAX_LENGTH}
        placeholder="이 Session에 대한 메모를 남겨 보세요."
        spellCheck={false}
      />
      <div className={styles.actions}>
        <span className={styles.counter}>
          {editor.value.length}/{NOTE_MAX_LENGTH}
        </span>
        <IconButton
          text="지우기"
          variant="surface"
          disabled={clearDisabled}
          onClick={() => void handleClear()}
        />
        <IconButton
          text={editor.saving || saving ? "저장 중…" : "저장"}
          variant="solid"
          tone="primary"
          disabled={saveDisabled}
          onClick={() => void handleSave()}
        />
      </div>
    </section>
  );
}

function equalTrimmed(value: string, savedValue: string): boolean {
  return value.trim() === savedValue.trim();
}
